import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, ScoreRing, CompetencyBar, TrustBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Scale, Trophy, Sparkles, Loader2, ThumbsUp, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

export default function Compare() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [cands, setCands] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const idsParam = params.get("ids") || "";
  useEffect(() => {
    api.get(`/jobs/${jobId}/candidates`).then((r) => {
      setCands(r.data);
      const preIds = idsParam.split(",").filter(Boolean);
      if (preIds.length) {
        const valid = new Set(r.data.map((c) => c.id));
        setSel(new Set(preIds.filter((id) => valid.has(id)).slice(0, 5)));
      }
    }).finally(() => setLoading(false));
  }, [jobId, idsParam]);

  const toggle = (id) => setSel((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else if (n.size < 5) n.add(id);
    else toast.message("Up to 5 candidates");
    return n;
  });

  const compare = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/candidates/compare", { candidate_ids: [...sel] });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Comparison failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <LoadingState label="Loading candidates…" />;

  const byId = Object.fromEntries((result?.candidates || []).map((c) => [c.id, c]));
  const ranked = (result?.analysis?.ranking || []).slice().sort((a, b) => a.rank - b.rank);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-6xl mx-auto space-y-6">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase font-mono tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5"><Scale className="h-3.5 w-3.5" /> Candidate comparison</div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">Pick 2–5 candidates to compare</h1>
        </div>
        <Button onClick={compare} disabled={busy || sel.size < 2} data-testid="run-compare">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Compare {sel.size > 0 ? `(${sel.size})` : ""}
        </Button>
      </div>

      {/* Selectable candidate chips */}
      <div className="flex flex-wrap gap-2" data-testid="compare-picker">
        {cands.map((c) => (
          <button key={c.id} onClick={() => toggle(c.id)} data-selected={sel.has(c.id)} data-testid={`pick-${c.id}`}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors data-[selected=true]:border-primary data-[selected=true]:bg-primary/10 border-border/60 text-foreground/80 hover:border-primary/40">
            {c.name} <span className="font-mono text-xs text-muted-foreground">{c.overall_score ?? "—"}</span>
          </button>
        ))}
      </div>

      {result && (
        <div className="space-y-6" data-testid="compare-result">
          {/* AI ranking */}
          <div className="rounded-xl border border-border/60 bg-card p-5 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold"><Trophy className="h-4 w-4 text-primary" /> Recommended ranking</div>
            {result.analysis?.summary && <p className="text-sm text-foreground/85 leading-relaxed">{result.analysis.summary}</p>}
            <ol className="space-y-2">
              {ranked.map((r) => (
                <li key={r.id} className="flex items-start gap-3 text-sm">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-primary/15 text-primary font-mono text-xs flex items-center justify-center">{r.rank}</span>
                  <span><span className="font-medium">{byId[r.id]?.name || "Candidate"}</span> — <span className="text-muted-foreground">{r.reason}</span></span>
                </li>
              ))}
            </ol>
          </div>

          {/* Side-by-side grid */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${result.candidates.length}, minmax(220px, 1fr))` }}>
            {result.candidates.map((c) => {
              const notes = result.analysis?.notes?.[c.id] || {};
              return (
                <div key={c.id} className="rounded-xl border border-border/60 bg-card p-4 space-y-3" data-testid={`compare-col-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <ScoreRing score={c.overall_score} size={48} label="fit" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      {c.confidence && <TrustBadge type={c.confidence === "high" ? "high" : "medium"}>{c.confidence}</TrustBadge>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {(c.competencies || []).slice(0, 4).map((comp, i) => <CompetencyBar key={i} {...comp} />)}
                  </div>
                  {notes.strengths?.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-emerald-400 mb-1 flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> Strengths</div>
                      <ul className="text-xs text-foreground/80 space-y-0.5">{notes.strengths.map((s, i) => <li key={i}>• {s}</li>)}</ul>
                    </div>
                  )}
                  {notes.risks?.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-amber-400 mb-1 flex items-center gap-1"><TriangleAlert className="h-3 w-3" /> Risks</div>
                      <ul className="text-xs text-foreground/80 space-y-0.5">{notes.risks.map((s, i) => <li key={i}>• {s}</li>)}</ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
